// @ts-check
/**
 * @file LoginScreen.jsx
 * @location src/components/auth/LoginScreen.jsx
 * @role 비로그인 게이트 — 이메일/패스워드 표준 로그인.
 *
 *  sub: 'login' / 'signup' / 'reset'.
 *  매직링크(signInWithOtp)는 표준 비번 통일로 폐기 — 매일 쓰는 도구엔 매번 메일 클릭이 무겁고,
 *  비번 없는 유저를 만들어 개인정보 변경 재인증을 막았음. 팀 연결은 초대(코드/이메일)가 담당.
 *  Supabase Auth callback: detectSessionInUrl 가 재설정 콜백 자동 처리.
 */
import { useState } from 'react';
import { PasswordField } from './PasswordField.jsx';
import { LegalLinks } from './LegalLinks.jsx';
import { localizeAuthError } from '../../integrations/supabase/errorMessage.js';
import { validatePassword, PASSWORD_HINT } from '../../utils/passwordPolicy.js';

/**
 * @typedef {(email: string) => Promise<void>} EmailHandler
 * @typedef {(email: string, password: string) => Promise<void>} PasswordHandler
 * @typedef {() => Promise<void>} VoidPromiseHandler
 */

/**
 * @param {{
 *   onSignInWithPassword: PasswordHandler,
 *   onSignUpWithPassword: PasswordHandler,
 *   onSendPasswordReset: EmailHandler,
 *   onSignInWithGoogle?: VoidPromiseHandler,
 *   onBack?: () => void,
 *   initialSignup?: boolean,
 * }} props
 */
export function LoginScreen({
  onSignInWithPassword,
  onSignUpWithPassword,
  onSendPasswordReset,
  onSignInWithGoogle,
  onBack,
  initialSignup = false,
}) {
  const [pwSub, setPwSub] = useState(initialSignup ? 'signup' : 'login');   // 'login' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [agreed, setAgreed] = useState(false);

  const reset = () => { setErr(''); setSent(false); setPassword(''); setAgreed(false); };

  const submitGoogle = async () => {
    if (busy || !onSignInWithGoogle) return;
    setBusy(true); setErr('');
    try {
      await onSignInWithGoogle();
      // 성공 시 Supabase 가 OAuth 리다이렉트 — busy 풀지 않음(페이지 이탈)
    } catch (e) {
      setErr(localizeAuthError(e, 'Google 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if (busy) return;
    const addr = email.trim();
    if (!addr) { setErr('이메일 주소를 입력해 주세요'); return; }
    setBusy(true); setErr('');
    try {
      if (pwSub === 'login') {
        await onSignInWithPassword(addr, password);
      } else if (pwSub === 'signup') {
        const v = validatePassword(password);
        if (!v.ok) { setErr(v.message); setBusy(false); return; }
        await onSignUpWithPassword(addr, password);
        setSent(true);
      } else {
        await onSendPasswordReset(addr);
        setSent(true);
      }
    } catch (e) {
      setErr(localizeAuthError(e, '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="modal auth-modal">
        <div className="mhd"><span className="mtit">온점</span></div>
        <div className="mbody">
          {onBack && (
            <button type="button" className="auth-link" style={{ alignSelf: 'flex-start' }} onClick={onBack}>
              ← 처음으로
            </button>
          )}

          {!sent && (
            <>
              {pwSub !== 'reset' && onSignInWithGoogle && (
                <>
                  <button
                    type="button"
                    className="auth-oauth"
                    onClick={submitGoogle}
                    disabled={busy}
                    aria-label="Google로 계속하기"
                  >
                    <svg className="auth-oauth-ico" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                      <path fill="#FBBC05" d="M3.964 10.708A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.708V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.332z" />
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.29C4.672 5.166 6.656 3.58 9 3.58z" />
                    </svg>
                    Google로 계속하기
                  </button>
                  <div className="auth-divider" aria-hidden="true"><span>또는</span></div>
                </>
              )}
              {pwSub !== 'reset' && (
                <div className="seg" role="tablist">
                  <button
                    type="button"
                    className={`seg-opt${pwSub === 'login' ? ' on' : ''}`}
                    role="tab" aria-selected={pwSub === 'login'}
                    onClick={() => { setPwSub('login'); reset(); }}
                  >
                    로그인
                  </button>
                  <button
                    type="button"
                    className={`seg-opt${pwSub === 'signup' ? ' on' : ''}`}
                    role="tab" aria-selected={pwSub === 'signup'}
                    onClick={() => { setPwSub('signup'); reset(); }}
                  >
                    회원가입
                  </button>
                </div>
              )}
              {pwSub !== 'login' && (
                <p className="auth-lead">
                  {pwSub === 'signup' && `비밀번호는 ${PASSWORD_HINT}으로 설정해 주세요.`}
                  {pwSub === 'reset' && '등록하신 이메일 주소로 비밀번호 재설정 링크를 보내드립니다.'}
                </p>
              )}
              <label className="flabel" htmlFor="auth-email-p">이메일 주소</label>
              <input
                id="auth-email-p"
                className="finput"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {pwSub !== 'reset' && (
                <PasswordField
                  value={password}
                  onChange={setPassword}
                  autoComplete={pwSub === 'signup' ? 'new-password' : 'current-password'}
                />
              )}
              {pwSub === 'signup' && (
                <label className="auth-agree">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                  <span>
                    <a href="/legal/terms.html" target="_blank" rel="noopener noreferrer">이용약관</a>
                    ·
                    <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>
                    에 동의합니다
                  </span>
                </label>
              )}
              {err && <div className="auth-err">{err}</div>}
              <button
                type="button"
                className="auth-primary btnp"
                onClick={submitPassword}
                disabled={busy || (pwSub === 'signup' && !agreed)}
              >
                {pwSub === 'login' && '로그인'}
                {pwSub === 'signup' && '회원가입'}
                {pwSub === 'reset' && '재설정 메일 보내기'}
              </button>

              <div className="auth-sublinks">
                {pwSub === 'login' && (
                  <button type="button" className="auth-link" onClick={() => { setPwSub('reset'); reset(); }}>비밀번호를 잊으셨나요?</button>
                )}
                {pwSub === 'reset' && (
                  <button type="button" className="auth-link" onClick={() => { setPwSub('login'); reset(); }}>로그인으로 돌아가기</button>
                )}
              </div>
            </>
          )}

          {sent && (
            <p className="auth-lead auth-sent">
              <strong>{email.trim()}</strong> 으로 메일을 보냈습니다.<br />
              받은편지함을 확인해 주세요.<br />
              <span className="auth-sub">도착하지 않으면 스팸 메일함도 확인해 주세요.</span>
            </p>
          )}

          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
